import { css } from '@emotion/react';
import maplibregl, { LngLat, Map as MapLibreMapInstance, PositionAnchor } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { globalConfig } from '~src/context/config';

import { Coordinates } from '../../api/api';

type MapLibreMapRefObject = React.MutableRefObject<MapLibreMapInstance | undefined>;

function lnglat(coordinates: Coordinates) {
  return new LngLat(coordinates.longitude, coordinates.latitude);
}

export function fitBounds(mapInstance: MapLibreMapInstance, coordinatesArray: Coordinates[]) {
  const bounds = new maplibregl.LngLatBounds();

  coordinatesArray.forEach((coordinates: Coordinates) => {
    bounds.extend(lnglat(coordinates));
  });

  mapInstance.fitBounds(bounds, { maxZoom: 16, padding: 40 });
}

interface MapLibreMarkerProps {
  anchor?: PositionAnchor;
  coordinates: Coordinates;
  mapRef: MapLibreMapRefObject;
  zIndex?: string;
  onClick?: () => void;
}

export const MapLibreMarker: React.FC<React.PropsWithChildren<MapLibreMarkerProps>> = ({
  anchor,
  children,
  coordinates,
  mapRef,
  zIndex,
  onClick,
}) => {
  const ref = React.useRef<HTMLDivElement>();

  if (!ref.current) {
    ref.current = document.createElement('div');
  }

  ref.current.style.zIndex = zIndex ?? 'auto';

  React.useEffect(() => {
    if (!onClick) {
      return;
    }

    const clickHandler = () => onClick();

    ref.current!.addEventListener('click', clickHandler);
    return () => {
      ref.current!.removeEventListener('click', clickHandler);
    };
  }, [onClick]);

  React.useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    const marker = new maplibregl.Marker({
      anchor,
      element: ref.current,
    });

    /*
     * HACK: For some reason mapbox sets static default on label, remove it.
     * This should be called after the constructor.
     */
    ref.current!.removeAttribute('aria-label');

    marker.setLngLat(lnglat(coordinates)).addTo(mapRef.current!);
  }, [mapRef.current]);

  return createPortal(children, ref.current);
};

interface MapLibreMapProps {
  center?: Coordinates;
  defaultZoom: number;
  mapRef: MapLibreMapRefObject;
  onInit: () => void;
  onZoom: (newZoom: number) => void;
}

export const MapLibreMap: React.FC<React.PropsWithChildren<MapLibreMapProps>> = ({
  center,
  defaultZoom,
  children,
  mapRef,
  onInit,
  onZoom,
}) => {
  const [loaded, setLoaded] = React.useState<boolean>(false);

  const containerRef = React.useRef(null);

  React.useEffect(() => {
    try {
      const mapInstance = new maplibregl.Map({
        container: containerRef.current!,
        style: `https://api.maptiler.com/maps/2c8c0236-8845-4496-9286-3c8affc287fc/style.json?key=${globalConfig.maptilerToken}`,
        center: center ? lnglat(center) : undefined,
        zoom: defaultZoom,
      });

      /*
       * HACK: For some reason mapbox sets static default on label, remove it.
       * This should be called after the constructor.
       */
      mapInstance.getCanvas().removeAttribute('aria-label');

      mapRef.current = mapInstance;

      mapInstance.on('load', () => {
        onInit();
      });

      mapInstance.on('zoomend', () => {
        onZoom(mapInstance.getZoom());
      });

      setLoaded(true); // reload component
    } catch (error) {
      // For the moment just display the error in console.
      // eslint-disable-next-line
      console.error(error);
    }
  }, []);

  React.useEffect(() => {
    /* Update center */
    if (!mapRef.current || !center) {
      return;
    }

    const oldCenter = mapRef.current.getCenter();

    if (oldCenter.lng !== center.longitude || oldCenter.lat !== center.latitude) {
      mapRef.current.easeTo({ center: lnglat(center) });
    }
  }, [center]);

  return (
    <>
      <div ref={containerRef} css={styles.container} data-test="map" />

      {loaded && children}
    </>
  );
};

const styles = {
  container: css({
    height: '100%',
    /*
     * 1. Fallback background in case we catch webgl exceptions.
     * 2. Color is out of palette.
     */
    backgroundColor: '#eee',
  }),
};
